import React, { useEffect, useState } from "react";
import {
  Box, Button, Typography, Dialog, DialogActions, DialogContent, DialogTitle,
  useMediaQuery, useTheme, Alert
} from "@mui/material";
import { useBanners } from "../../hooks/useBanners";
import BannerTable from "../../components/BannerTable";
import AddBannerForm from "../../components/AddBannerForm";
import { usePageContext } from "../../context/PageContext";
import { useTranslation } from "react-i18next";

const Banners = () => {
  const { t } = useTranslation("settings");
  const { currentPage, setCurrentPage } = usePageContext();
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { data, error, refetch, updateMutation, deleteMutation, isLoading } =
    useBanners(currentPage, pageSize, search);

  useEffect(() => {
    if (error) setErrorMessage(t("bannersPage.loadError"));
    else setErrorMessage(null);
  }, [error, t]);

  const handleEditBanner = (banner) => {
    setSelectedBanner(banner);
    setErrorMessage(null);
  };

  const handleDeleteBanner = (id) => {
    setConfirmDelete(id);
    setErrorMessage(null);
  };

  const confirmDeleteBanner = () => {
    if (!confirmDelete) return;
    setIsProcessing(true);
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => {
        setConfirmDelete(null);
        refetch();
      },
      onError: (err) => {
        setErrorMessage(err?.response?.data?.message || t("bannersPage.deleteError"));
      },
      onSettled: () => setIsProcessing(false),
    });
  };

  // Toggle status
  const toggleStatus = (b) => {
    setIsProcessing(true);
    updateMutation.mutate(
      { id: b.id, status: b.status ? 0 : 1 },
      {
        onSuccess: () => refetch(),
        onError: (err) => setErrorMessage(err?.response?.data?.message || t("bannersPage.statusError")),
        onSettled: () => setIsProcessing(false),
      }
    );
  };

  // Ações em massa
  const bulkActivate = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => updateMutation.mutateAsync({ id, status: 1 })));
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || t("bannersPage.activateError"));
    } finally { setIsProcessing(false); }
  };
  const bulkDeactivate = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => updateMutation.mutateAsync({ id, status: 0 })));
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || t("bannersPage.deactivateError"));
    } finally { setIsProcessing(false); }
  };
  const bulkDelete = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => deleteMutation.mutateAsync(id)));
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || t("bannersPage.bulkDeleteError"));
    } finally { setIsProcessing(false); }
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage + 1);
  const handlePageSizeChange = (newPageSize) => setPageSize(newPageSize);
  const handleSearch = (term) => { setSearch(term); refetch(); };
  const handleCloseForm = () => { setSelectedBanner(null); setErrorMessage(null); };

  return (
    <Box sx={{ paddingTop: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom>
          {t("bannersPage.heading")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          fullWidth={isMobile}
          onClick={() => setSelectedBanner({})}
        >
          {t("bannersPage.addBannerButton")}
        </Button>
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {selectedBanner ? (
        <AddBannerForm
          bannerData={selectedBanner}
          isLoading={isLoading || isProcessing}
          onClose={handleCloseForm}
          onSubmit={(updated) => {
            setIsProcessing(true);
            updateMutation.mutate(updated, {
              onSuccess: () => { refetch(); handleCloseForm(); },
              onError: (err) =>
                setErrorMessage(err?.response?.data?.message || t("bannersPage.saveError")),
              onSettled: () => setIsProcessing(false),
            });
          }}
        />
      ) : (
        <BannerTable
          banners={data?.banners || []}
          rowCount={data?.pagination?.total || 0}
          pageSize={pageSize}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSearch={handleSearch}
          isLoading={isLoading || isProcessing}
          onEditBanner={handleEditBanner}
          onDeleteBanner={handleDeleteBanner}
          onToggleStatus={toggleStatus}
          onBulkActivate={bulkActivate}
          onBulkDeactivate={bulkDeactivate}
          onBulkDelete={bulkDelete}
        />
      )}

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{t("bannersPage.deleteDialogTitle")}</DialogTitle>
        <DialogContent>
          <Typography>
            {isProcessing ? t("bannersPage.processingDelete") : t("bannersPage.deleteConfirm")}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!isProcessing && (
            <>
              <Button onClick={() => setConfirmDelete(null)}>{t("shared.cancel")}</Button>
              <Button color="error" onClick={confirmDeleteBanner}>{t("shared.delete")}</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Banners;
